import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { createChartXAxisLabel } from "utils/charts";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetOpportunityCostsByCommunityQuery } from "state/communities/communitiesApiSlice";

import EmsChartCard from "./EmsChartCard";

function OpportunityCostChart({ startDatetime, endDatetime, onDataStatus }) {
  const { t } = useTranslation(["community"]);
  const community = useSelector(selectCurrentCommunity);
  const communityId = community?.id;

  const hasDateFilter = !!(startDatetime || endDatetime);

  const queryArgs = {
    communityId,
    startDatetime,
    endDatetime,
    limit: hasDateFilter ? 2000 : 1000,
    offset: 0,
  };
  const queryOpts = { skip: !communityId, pollingInterval: 60000 };

  const { data: costList = [], isLoading } = useGetOpportunityCostsByCommunityQuery(
    queryArgs,
    queryOpts
  );

  const isEmpty = !isLoading && costList.length === 0;

  useEffect(() => {
    if (!isLoading && onDataStatus) onDataStatus(!isEmpty);
  }, [isLoading, isEmpty, onDataStatus]);

  const { series, labels } = useMemo(() => {
    if (costList.length === 0) return { series: [], labels: [] };

    const sorted = [...costList].sort(
      (a, b) => new Date(a.datetime_timestamp) - new Date(b.datetime_timestamp)
    );
    const lbls = sorted.map((r) => createChartXAxisLabel(r.datetime_timestamp, "MIN15"));

    return {
      labels: lbls,
      series: [
        {
          name: t("unified_ems.scenario_chart_l_buy"),
          data: sorted.map((r) => (r.l_buy !== null ? Number(Number(r.l_buy).toFixed(4)) : null)),
          color: "var(--info)",
        },
        {
          name: t("unified_ems.scenario_chart_l_sell"),
          data: sorted.map((r) => (r.l_sell !== null ? Number(Number(r.l_sell).toFixed(4)) : null)),
          color: "var(--accent)",
        },
      ],
    };
  }, [costList, t]);

  if (isLoading || isEmpty) return null;

  const legend = series.map((s) => ({ label: s.name, color: s.color }));

  return (
    <EmsChartCard
      title={t("unified_ems.opportunity_cost_chart_title")}
      legend={legend}
      series={series}
      labels={labels}
      unit="€/kWh"
      type="line"
    />
  );
}

OpportunityCostChart.defaultProps = {
  startDatetime: undefined,
  endDatetime: undefined,
  onDataStatus: undefined,
};

OpportunityCostChart.propTypes = {
  // moduleType is passed by ModuleView for consistency with other charts; not used in
  // this community-wide query but kept as required to maintain a uniform prop interface.
  // eslint-disable-next-line react/no-unused-prop-types
  moduleType: PropTypes.string.isRequired,
  startDatetime: PropTypes.string,
  endDatetime: PropTypes.string,
  onDataStatus: PropTypes.func,
};

export default OpportunityCostChart;
